import { useCallback, useMemo } from "react";

import { EMPTY_HIDDEN_TYPES } from "../constants";
import { useMapLayers } from "../contexts/map-layers-context";

import type maplibregl from "maplibre-gl";

export type LayerKey = string;

export function useLayer(key: LayerKey) {
  const {
    layerFilters,
    layerVisibility,
    layerHiddenTypes,
    setLayerFilter,
    setLayerHidden,
    setLayerHiddenTypes,
    toggleLayerHiddenType,
  } = useMapLayers();

  const filter = layerFilters[key];
  const hidden = !layerVisibility[key];
  const hiddenTypes = useMemo(() => layerHiddenTypes[key] ?? EMPTY_HIDDEN_TYPES, [key, layerHiddenTypes]);

  const setFilter = useCallback(
    (f: maplibregl.FilterSpecification | undefined) => setLayerFilter(key, f),
    [key, setLayerFilter],
  );
  const setHidden = useCallback(
    (nextHidden: boolean) => setLayerHidden(key, nextHidden),
    [key, setLayerHidden],
  );
  const setHiddenTypes = useCallback(
    (nextHiddenTypes: string[]) => setLayerHiddenTypes(key, nextHiddenTypes),
    [key, setLayerHiddenTypes],
  );
  const toggleHiddenType = useCallback(
    (type: string) => toggleLayerHiddenType(key, type),
    [key, toggleLayerHiddenType],
  );
  const isTypeHidden = useCallback(
    (type: string) => hiddenTypes.includes(type),
    [hiddenTypes],
  );

  return useMemo(
    () => ({
      key,
      filter,
      hidden,
      hiddenTypes,
      setFilter,
      setHidden,
      setHiddenTypes,
      toggleHiddenType,
      isTypeHidden,
    }),
    [key, filter, hidden, hiddenTypes, setFilter, setHidden, setHiddenTypes, toggleHiddenType, isTypeHidden],
  );
}
